import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import {
  Drawer,
  Box,
  makeStyles,
  OutlinedInput,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import FilterIcon from '@material-ui/icons/Filter';
import InfoIcon from '@material-ui/icons/Info';
import classNames from 'classnames';
import { useAtom } from 'jotai';

import { Typography } from '@clef/client-library';
import { DatasetVersion } from '@clef/shared/types';

import { getDateNumber } from '@/utils';
import CLEF_PATH from '@/constants/path';
import ModelCountSvg from '@/images/project/model_count.svg';
import { useDatasetExportedWithVersionsQuery } from '@/serverStore/dataset';
import {
  historyListOffsetAtom,
  hideAutoGeneratedSnapshotAtom,
} from '@/uiStates/datasetSnapshot/pageUIStates';

export interface VersionHistoryDrawerProps {}

export const rightDrawerWidth = 340;

export const useStyles = makeStyles(theme => ({
  rightDrawer: {
    position: 'absolute',
    width: rightDrawerWidth,
    borderRadius: 0,
    borderLeft: `2px solid ${theme.palette.purple[50]}`,
    boxShadow: 'none !important',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
  },
  drawerHeader: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 56,
  },
  snapshotList: {
    flexGrow: 1,
    overflowY: 'auto',
  },
  snapshotItem: {
    maxHeight: 156,
    margin: theme.spacing(0, 3, 3, 3),
    padding: theme.spacing(5),
    // border: `1px solid ${theme.palette.greyModern[300]}`,
    borderRadius: 10,
    backgroundColor: theme.palette.greyModern[50],
    cursor: 'pointer',
  },
  chosenItem: {
    backgroundColor: theme.palette.purple[50],
  },
  itemInfo: {
    marginRight: theme.spacing(4),

    '& p': {
      fontWeight: 700,
      fontSize: 14,
      color: theme.palette.greyModern[800],
    },
  },
  versionSearchContainer: {
    borderRadius: 6,
    minWidth: 200,
    margin: theme.spacing(0, 3, 3, 3),
    height: 40,
  },
  versionSearchInput: {
    padding: theme.spacing(2, 0, 2, 3),
  },
  itemDetails: {
    marginTop: theme.spacing(2),
    fontSize: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 'normal', // adjust line height
    color: '#4D5761',
  },
  moreInfoWrapper: {
    marginTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.purple[100]}`,
    paddingTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
  },
  counts: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  statLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  drawerFooter: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 42,
    fontSize: 16,
    fontWeight: 500,
    borderTop: `1px solid ${theme.palette.greyModern[300]}`,
    paddingLeft: theme.spacing(4),
  },
}));

const VersionHistoryDrawer: React.FC<VersionHistoryDrawerProps> = () => {
  const styles = useStyles();

  const history = useHistory();
  const { version } = useParams<{ version: string }>();

  const { data: datasetExported } = useDatasetExportedWithVersionsQuery({
    withCount: true,
    includeNotCompleted: true,
    includeFastEasy: true,
  });

  // for memory list scroll offset after refresh page
  const snapshotListRef = useRef<HTMLDivElement | null>(null);
  const [historyListOffset, setHistoryListOffset] = useAtom(historyListOffsetAtom);
  const chosenItemRef = useRef<HTMLDivElement>(null);

  const [hideAutoGeneratedSnapshot, setHideAutoGeneratedSnapshot] = useAtom(
    hideAutoGeneratedSnapshotAtom,
  );
  useEffect(() => {
    // firsttime load or refresh page, and current version is auto-generated snapshot, turn hide toggle off to show it
    if (
      historyListOffset === undefined &&
      datasetExported?.datasetVersions?.find(v => v.version === parseInt(version))?.fastEasyExport
    ) {
      setHideAutoGeneratedSnapshot(false);
    }
  }, [datasetExported?.datasetVersions, historyListOffset, setHideAutoGeneratedSnapshot, version]);

  const [searchText, setSearchText] = useState<string>('');
  const renderedVersions = useMemo(() => {
    let list = datasetExported?.datasetVersions;
    if (list && hideAutoGeneratedSnapshot) {
      list = list.filter(v => !v.fastEasyExport);
    }
    if (list && searchText) {
      list = list.filter(v => v.name.toLowerCase().includes(searchText.toLowerCase()));
    }
    return list;
  }, [datasetExported?.datasetVersions, searchText, hideAutoGeneratedSnapshot]);

  // remember scroll position when click snapshot item
  const handleSnapshotItemClick = (v: DatasetVersion) => {
    if (v.version === parseInt(version)) return;
    const scrollPosition = snapshotListRef ? snapshotListRef.current?.scrollTop : undefined;
    setHistoryListOffset(scrollPosition);
    history.push(`${CLEF_PATH.data.datasetSnapshot}/${v.version}`);
  };

  // locate the scroll position when switch between snapshots
  useEffect(() => {
    if (historyListOffset !== undefined && snapshotListRef.current) {
      snapshotListRef.current.scrollTop = historyListOffset;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // firsttime load or refresh page, scroll current version into view
    if (historyListOffset === undefined && chosenItemRef.current) {
      chosenItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyListOffset, chosenItemRef.current]);

  return (
    <Drawer
      open
      variant="persistent"
      classes={{
        paper: styles.rightDrawer,
      }}
      anchor="right"
    >
      <Typography variant="h3" className={styles.drawerHeader}>
        Snapshots
      </Typography>

      <OutlinedInput
        value={searchText}
        onChange={({ target: { value } }) => setSearchText(value)}
        placeholder={t('Search by snapshot name')}
        inputProps={{
          className: styles.versionSearchInput,
        }}
        className={styles.versionSearchContainer}
      />
      <div className={styles.snapshotList} ref={snapshotListRef}>
        {renderedVersions
          ?.sort((a, b) => getDateNumber(b.creationTime) - getDateNumber(a.creationTime))
          .map(v => (
            <div
              ref={v.version === parseInt(version) ? chosenItemRef : null}
              key={v.id}
              className={classNames(
                styles.snapshotItem,
                v.version === parseInt(version) && styles.chosenItem,
              )}
              onClick={e => {
                e.stopPropagation();
                handleSnapshotItemClick(v);
              }}
            >
              <Box className={styles.itemInfo}>
                <Typography maxWidth={230}>{v.name}</Typography>
                {v.details && <Box className={styles.itemDetails}>{v.details}</Box>}
              </Box>
              <Box className={styles.moreInfoWrapper}>
                <Typography variant="body_medium">
                  {v.fastEasyExport ? t('Auto') : v.creator ? `By ${v.creator}` : ''}
                </Typography>
                <Box className={styles.counts}>
                  <Box className={styles.statLabel}>
                    <FilterIcon fontSize="small" htmlColor="#4D5761" />
                    <Typography variant="body_medium">{v.count}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <img src={ModelCountSvg} width={20} height={20} />
                    <Typography variant="body1">{v.modelsCount ?? 0}</Typography>
                  </Box>
                </Box>
              </Box>
            </div>
          ))}
      </div>
      <Box className={styles.drawerFooter}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hideAutoGeneratedSnapshot}
              color="primary"
              onClick={() => setHideAutoGeneratedSnapshot(prev => !prev)}
            />
          }
          label={<Box>{t('Hide auto-generated snapshots')}</Box>}
        />
        <Tooltip
          placement="top"
          title={t('LandingLens will automatically take snapshots while training a model.')}
          arrow
        >
          <InfoIcon fontSize="small" htmlColor="#98C6FE" />
        </Tooltip>
      </Box>
    </Drawer>
  );
};

export default VersionHistoryDrawer;
